import { redirect } from 'react-router-dom';
import type { RouterContextProvider } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { LdtAppRepository } from '@ldt/api/app_repository';
import { AppRepository } from 'src/api/app_repository';
import { AuthenticationService } from 'src/api/authentication_service';
import { DependencyContainer } from 'src/app/dependency_container';
import { orderKeys } from 'src/constants/orders';
import {
  LDT_DEFAULT_ORGANIZATION_NAME,
  organizationKeys,
} from 'src/constants/organizations';
import { OrganizationListFieldsFragment } from 'src/types';
import { isCustomerSuccessOrSalesUser } from 'src/utils';

function getOrgPathParam(pathname: string): string | null {
  const pathSegments = pathname.split('/').filter((segment) => segment !== '');
  return pathSegments.length > 0 ? pathSegments[0] : null;
}

function isAtRoot(pathname: string, orgPathParam: string | null): boolean {
  return !orgPathParam || pathname === '/';
}

/**
 * Runs after auth middleware on all authenticated routes.
 *
 * - CS/Sales: bootstrap Organization header, redirect off `/`, validate URL org,
 *   fetch org list, resolve effective organization for the selection.
 * - Non-CS / Non-LDT: resolve effective organization (never set Organization header),
 *   redirect off `/` or when URL org does not match.
 */
export function createOrganizationMiddleware(queryClient: QueryClient) {
  return async (
    { request }: { request: Request; context: RouterContextProvider },
    next: () => Promise<Response>,
  ) => {
    const pathname = new URL(request.url).pathname;
    const orgPathParam = getOrgPathParam(pathname);
    const atRoot = isAtRoot(pathname, orgPathParam);

    const dependencyContainer = DependencyContainer.getInstance();
    const appRepository = dependencyContainer.appRepository;

    try {
      if (isCustomerSuccessOrSalesUser()) {
        await handleCsSalesOrganization(
          queryClient,
          appRepository as LdtAppRepository,
          request,
          orgPathParam,
          atRoot,
        );
        await next();
        return;
      }

      await handleClinicianOrganization(
        queryClient,
        appRepository,
        orgPathParam,
        atRoot,
      );
      await next();
    } catch (err) {
      if (err instanceof Response && err.status === 302) throw err;

      throw err;
    }
  };
}

function clearOrganizationScopedQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: orderKeys.all });
  queryClient.removeQueries({ queryKey: ['members'] });
  queryClient.removeQueries({ queryKey: ['invites'] });
  queryClient.removeQueries({ queryKey: ['assignableRoles'] });
}

async function handleCsSalesOrganization(
  queryClient: QueryClient,
  appRepository: LdtAppRepository,
  request: Request,
  orgPathParam: string | null,
  atRoot: boolean,
): Promise<void> {
  if (!AuthenticationService.getInstance().getOrganizationHeader()) {
    AuthenticationService.getInstance().setOrganizationHeader(
      LDT_DEFAULT_ORGANIZATION_NAME,
    );
  }

  if (atRoot) {
    throw redirect(`/${LDT_DEFAULT_ORGANIZATION_NAME}/orders`);
  }

  const organizations = await queryClient.ensureQueryData<
    OrganizationListFieldsFragment[]
  >({
    queryKey: organizationKeys.lists,
    queryFn: () => appRepository.getOrganizations(),
  });

  const url = new URL(request.url);
  const pathname = url.pathname;
  const orgQueryParam = url.searchParams.get('organization');
  const getValidOrgSelection = (
    name: string,
  ): OrganizationListFieldsFragment | undefined =>
    organizations.find((o) => o.name === name);

  let selectedOrganizationName: string | undefined;

  if (orgQueryParam && getValidOrgSelection(orgQueryParam)) {
    selectedOrganizationName = orgQueryParam;
    const newPath = pathname.replace(`/${orgPathParam}`, `/${orgQueryParam}`);
    throw redirect(newPath);
  } else if (orgPathParam && getValidOrgSelection(orgPathParam)) {
    selectedOrganizationName = orgPathParam;
  } else {
    throw redirect(`/${LDT_DEFAULT_ORGANIZATION_NAME}/orders`);
  }

  const authService = AuthenticationService.getInstance();
  const previousHeader = authService.getOrganizationHeader();

  authService.setOrganizationHeader(selectedOrganizationName);

  if (previousHeader !== selectedOrganizationName) {
    clearOrganizationScopedQueries(queryClient);
  }

  const effectiveOrganization = await queryClient.ensureQueryData({
    queryKey: organizationKeys.effective(selectedOrganizationName),
    queryFn: () => appRepository.getEffectiveOrganization(),
  });

  AuthenticationService.getInstance().setEffectiveOrganization(
    effectiveOrganization,
  );
}

async function handleClinicianOrganization(
  queryClient: QueryClient,
  appRepository: AppRepository,
  orgPathParam: string | null,
  atRoot: boolean,
): Promise<void> {
  const effectiveOrganization = await queryClient.ensureQueryData({
    queryKey: organizationKeys.effective(null),
    queryFn: () => appRepository.getEffectiveOrganization(),
  });

  AuthenticationService.getInstance().setEffectiveOrganization(
    effectiveOrganization,
  );

  const orgName = effectiveOrganization.name;
  if (!orgName) {
    throw new Error('Effective organization is missing a name');
  }

  if (atRoot || orgPathParam !== orgName) {
    throw redirect(`/${orgName}/orders`);
  }
}
